import { useState, useMemo } from 'react'
import { Box, Typography, Stack, Collapse, TextField, InputAdornment, IconButton } from '@mui/material'
import { ExpandMoreRounded, ExpandLessRounded, CheckRounded, SearchRounded, ClearRounded, ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { styles } from './warehouseStorageStyles'

const PAGE_SIZE = 10

export default function FOFilterSection ({ orders, selected, onChange }) {
  const { t_ } = useSmartTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const safeOrders = orders

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? safeOrders.filter(o => o.name.toLowerCase().includes(q)) : safeOrders
  }, [safeOrders, search])

  if (safeOrders.length === 0) return null

  const count = selected.length

  const toggle = (id) => {
    const next = selected.some(s => s.id === id)
      ? selected.filter(s => s.id !== id)
      : [...selected, safeOrders.find(o => o.id === id)]
    onChange(next)
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const pageItems = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  const handleSearch = (value) => {
    setSearch(value)
    setPage(0)
  }

  return (
    <Box sx={{
      flexShrink: 0,
      borderRadius: 1.5,
      border: '1px solid rgba(255,255,255,0.07)',
      overflow: 'hidden'
    }}
    >
      {/* Header */}
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.2,
          py: 1,
          cursor: 'pointer',
          minHeight: 52,
          bgcolor: open ? 'rgba(255,255,255,0.04)' : 'transparent',
          userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
        }}
      >
        <Stack direction='row' alignItems='center' spacing={0.8}>
          <Typography sx={{
            fontSize: '0.8rem',
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '0.07em'
          }}
          >
            {t_('Fabrication orders')}
          </Typography>
          {count > 0 && (
            <Box sx={{
              bgcolor: 'rgba(127,225,167,0.15)',
              color: '#7FE1A7',
              border: '1px solid rgba(127,225,167,0.4)',
              borderRadius: 1,
              px: 0.6,
              fontSize: '0.65rem',
              fontWeight: 700,
              lineHeight: 1.6
            }}
            >
              {count}
            </Box>
          )}
        </Stack>
        {open
          ? <ExpandLessRounded sx={{ fontSize: 18, color: '#555' }} />
          : <ExpandMoreRounded sx={{ fontSize: 18, color: '#555' }} />}
      </Box>

      <Collapse in={open}>
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>

          {/* Search */}
          <Box sx={{ px: 1, py: 0.8 }} onClick={e => e.stopPropagation()}>
            <TextField
              size='small'
              placeholder={t_('Search...')}
              value={search}
              onChange={e => handleSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchRounded sx={{ fontSize: 16, color: '#555' }} />
                  </InputAdornment>
                ),
                endAdornment: search
                  ? (
                    <InputAdornment position='end'>
                      <IconButton size='small' onClick={() => handleSearch('')} sx={{ p: 0.3, color: '#555' }}>
                        <ClearRounded sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                    )
                  : null
              }}
              sx={{
                ...styles.textField,
                '& .MuiOutlinedInput-root': {
                  ...styles.textField?.['& .MuiOutlinedInput-root'],
                  fontSize: '0.78rem'
                }
              }}
            />
          </Box>

          {/* Rows */}
          <Box>
            {pageItems.length === 0
              ? (
                <Typography sx={{ fontSize: '0.72rem', color: '#444', px: 1.5, py: 1 }}>—</Typography>
                )
              : pageItems.map(fo => {
                const isOn = selected.some(s => s.id === fo.id)
                return (
                  <Box
                    key={fo.id}
                    onClick={() => toggle(fo.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.2,
                      minHeight: 52,
                      cursor: 'pointer',
                      bgcolor: isOn ? 'rgba(127,225,167,0.07)' : 'transparent',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      '&:hover': { bgcolor: isOn ? 'rgba(127,225,167,0.1)' : 'rgba(255,255,255,0.04)' }
                    }}
                  >
                    <Typography sx={{ fontSize: '0.78rem', color: isOn ? '#7FE1A7' : '#bbb', ...styles.truncated, pr: 1 }}>
                      {fo.name}
                    </Typography>
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: isOn ? '#7FE1A7' : '#444',
                      bgcolor: isOn ? '#7FE1A7' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.15s'
                    }}
                    >
                      {isOn && <CheckRounded sx={{ fontSize: 14, color: '#000' }} />}
                    </Box>
                  </Box>
                )
              })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 0.5,
              py: 0.3,
              borderTop: '1px solid rgba(255,255,255,0.04)'
            }}
            >
              <IconButton
                size='small'
                disabled={safePage === 0}
                onClick={e => { e.stopPropagation(); setPage(safePage - 1) }}
                sx={{ color: '#7FE1A7', p: 0.5, minWidth: 32, minHeight: 32, opacity: safePage === 0 ? 0.3 : 1 }}
              >
                <ChevronLeftRounded sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography sx={{ fontSize: '0.68rem', color: '#555' }}>
                {safePage + 1} / {totalPages}
              </Typography>
              <IconButton
                size='small'
                disabled={safePage >= totalPages - 1}
                onClick={e => { e.stopPropagation(); setPage(safePage + 1) }}
                sx={{ color: '#7FE1A7', p: 0.5, minWidth: 32, minHeight: 32, opacity: safePage >= totalPages - 1 ? 0.3 : 1 }}
              >
                <ChevronRightRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
